import { forwardRef } from "react";

import Barcode from "react-barcode";

import dayjs from "dayjs";

import { getProductionUnitLabel } from "~/constants/units";
import type { ProductionBatch } from "~/types/production";
import type { BatchPallet } from "~/types/wms";

interface PalletLabelPrintProps {
  pallet: BatchPallet;
  batch: ProductionBatch;
  labelCount?: number;
}

interface SingleLabelProps {
  pallet: BatchPallet;
  batch: ProductionBatch;
}

function SingleLabel({ pallet, batch }: SingleLabelProps) {
  const productName =
    batch.product?.short_name || batch.product?.name || "Producto";
  const expirationDate = batch.expiry_date
    ? dayjs(batch.expiry_date).format("DD/MM/YYYY")
    : "N/A";
  const elaborationDate = batch.start_date
    ? dayjs(batch.start_date).format("DD/MM/YYYY")
    : dayjs().format("DD/MM/YYYY");

  return (
    <div className="pallet-label">
      {/* Three columns: product name left, details middle, sticker area right */}
      <div className="label-body">
        <div className="label-product">{productName}</div>

        <div className="label-details">
          <div className="detail-row">
            <span className="detail-label">Pallet:</span>
            <span className="detail-value">{pallet.sequence_number}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Cantidad:</span>
            <span className="detail-value">
              {pallet.initial_box_count}{" "}
              {getProductionUnitLabel(
                batch.production_unit,
                pallet.initial_box_count
              )}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Elaboración:</span>
            <span className="detail-value">{elaborationDate}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Vencimiento:</span>
            <span className="detail-value">{expirationDate}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Lote:</span>
            <span className="detail-value">
              {batch.lot_number || batch.batch_number}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Alergenos:</span>
            <span className="detail-value">
              {batch.contains_allergens ? "Con alergenos" : "Sin alergenos"}
            </span>
          </div>
        </div>

        <div className="sticker-area" />
      </div>

      {/* Barcode at bottom - full width */}
      <div className="barcode-section">
        <div className="barcode-container">
          <Barcode
            value={pallet.code}
            format="CODE128"
            width={2}
            height={50}
            displayValue={false}
            margin={0}
          />
        </div>
        <div className="barcode-value">{pallet.code}</div>
      </div>
    </div>
  );
}

export const PalletLabelPrint = forwardRef<
  HTMLDivElement,
  PalletLabelPrintProps
>(function PalletLabelPrint({ pallet, batch, labelCount = 1 }, ref) {
  const labels = Array.from({ length: labelCount }, (_, i) => i);

  return (
    <div ref={ref} className="print-container">
      <style>{`
          /* Screen styles - portrait preview matching 104mm x 200mm ratio */
          .print-container {
            padding: 16px;
            background: white;
          }

          .labels-grid {
            display: flex;
            flex-direction: column;
            gap: 16px;
            align-items: center;
          }

          .pallet-label {
            border: 2px dashed #ccc;
            border-radius: 8px;
            padding: 12px;
            background: white;
            width: 300px;
            height: 577px;
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
          }

          .label-body {
            display: flex;
            gap: 10px;
            flex: 1;
            min-height: 0;
          }

          .label-product {
            flex: 0 0 30%;
            font-size: 18px;
            font-weight: 900;
            color: #111;
            line-height: 1.2;
            display: flex;
            align-items: center;
            padding-right: 10px;
            border-right: 2px solid #e5e5e5;
            word-break: break-word;
          }

          .label-details {
            flex: 0 0 45%;
            font-size: 12px;
            display: flex;
            flex-direction: column;
            justify-content: center;
          }

          .sticker-area {
            flex: 0 0 25%;
            display: flex;
            align-items: center;
            justify-content: center;
            padding-left: 10px;
            border-left: 2px solid #e5e5e5;
          }

          .sticker-area::before {
            content: '';
            width: 60px;
            height: 60px;
            border: 2px dashed #ccc;
            border-radius: 4px;
          }

          .detail-row {
            display: flex;
            justify-content: space-between;
            padding: 4px 0;
            border-bottom: 1px solid #f0f0f0;
          }

          .detail-row:last-child {
            border-bottom: none;
          }

          .detail-label {
            font-weight: 500;
            color: #666;
          }

          .detail-value {
            color: #111;
            text-align: right;
            font-weight: 700;
          }

          .barcode-section {
            padding-top: 8px;
            border-top: 2px solid #e5e5e5;
            text-align: center;
            flex-shrink: 0;
          }

          .barcode-container {
            display: flex;
            justify-content: center;
            overflow: hidden;
          }

          .barcode-container svg {
            max-width: 100%;
            height: auto;
          }

          .barcode-value {
            font-size: 9px;
            color: #999;
            font-family: monospace;
            margin-top: 2px;
          }

          /*
           * Print: portrait label 104mm x 200mm.
           * Zebra driver: Ancho=104mm, Altura=200mm, Rotación=0°
           * No rotation needed - content is portrait.
           */
          @media print {
            @page {
              margin: 0;
            }

            html, body {
              width: 100vw;
              height: 100vh;
              margin: 0 !important;
              padding: 0 !important;
              overflow: hidden;
            }

            * {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            .print-container {
              padding: 0 !important;
              margin: 0 !important;
            }

            .labels-grid {
              display: block;
            }

            .pallet-label {
              position: absolute;
              top: 0;
              left: 0;
              width: 100vw;
              height: 100vh;
              border: none;
              border-radius: 0;
              padding: 3mm;
              box-sizing: border-box;
              display: flex;
              flex-direction: column;
              page-break-after: always;
            }

            .label-body {
              display: flex;
              gap: 3mm;
              flex: 1;
              min-height: 0;
            }

            .label-product {
              flex: 0 0 30%;
              font-size: 22pt;
              font-weight: 900;
              color: #000;
              line-height: 1.15;
              display: flex;
              align-items: center;
              padding-right: 3mm;
              border-right: 0.5mm solid #000;
              word-break: break-word;
            }

            .label-details {
              flex: 0 0 45%;
              font-size: 14pt;
              display: flex;
              flex-direction: column;
              justify-content: center;
            }

            .sticker-area {
              flex: 0 0 25%;
              display: flex;
              align-items: center;
              justify-content: center;
              padding-left: 3mm;
              border-left: 0.5mm solid #000;
            }

            .sticker-area::before {
              content: '';
              width: 20mm;
              height: 20mm;
              border: 0.3mm dashed #999;
            }

            .detail-row {
              display: flex;
              justify-content: space-between;
              padding: 1.5mm 0;
              border-bottom: 0.3mm solid #999;
            }

            .detail-row:last-child {
              border-bottom: none;
            }

            .detail-label {
              font-weight: 600;
              color: #000;
            }

            .detail-value {
              font-weight: 800;
              color: #000;
              text-align: right;
            }

            .barcode-section {
              padding-top: 3mm;
              border-top: 0.5mm solid #000;
              text-align: center;
              flex-shrink: 0;
            }

            .barcode-container {
              display: flex;
              justify-content: center;
            }

            .barcode-container svg {
              width: 90vw !important;
              height: 12mm !important;
            }

            .barcode-value {
              font-size: 9pt;
              color: #000;
              font-family: monospace;
              margin-top: 1mm;
              display: block;
            }
          }
        `}</style>

      <div className="labels-grid">
        {labels.map((index) => (
          <SingleLabel key={index} pallet={pallet} batch={batch} />
        ))}
      </div>
    </div>
  );
});

export default PalletLabelPrint;
